import React, { useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { ReactFlow, Handle, Position, BezierEdge } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import './style.css';
import { WindowSizeContext } from '/src/App';
import HelmetProvider from '/src/Components/HelmetProvider/HelmetProvider';
import CommonMisconceptionsData from '/src/services/Content/CommonMisconceptions';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { CircleX } from 'lucide-react';
import MyAccountIconDark from '/src/assets/scenarios-icon-dark.svg';
import MyAccountIconLight from '/src/assets/scenarios-icon-light.svg';
import greenDot from '/src/assets/Green-Dot-4.svg';


// FlowTrigger ensures DOM is ready before React Flow initializes
const FlowTrigger = ({ onMount }) => {
    useEffect(() => {
        onMount();
    }, [onMount]);
    return <div style={{ display: 'none' }} />;
};

// Custom node types for React Flow
const CategoryIconNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent'}}>
            <Handle type="source" position={Position.Bottom} style={{ visibility: 'hidden' }} />
        </div>
    );
};

const InfoBoxNode = () => {
    return (
        <div style={{ width: 5, height: 5, background: 'transparent' }}>
            <Handle type="target" position={Position.Top} style={{ visibility: 'hidden' }} />
        </div>
    );
};

const nodeTypes = {
    categoryIconNode: CategoryIconNode,
    infoBoxNode: InfoBoxNode
};

const CommonMisconceptions = () => {

    const { deviceType } = useContext(WindowSizeContext);

    const title = "What Most Homeowners Get Wrong About Climate Risk";
    const introText = "Most homeowners rely on assumptions that made sense years ago — but don’t match today’s costs, insurance realities, or climate patterns. You deserve the full picture. This page breaks down the most common misconceptions we hear from homeowners and homebuyers across the country.";

    // Sort categories by category_order
    const sortedCategories = useMemo(() => {
        return [...CommonMisconceptionsData].sort((a, b) => a.category_order - b.category_order);
    }, []);

    // State for selected category (default to first category)
    const [selectedCategory, setSelectedCategory] = useState(sortedCategories[0]);

    // State for drawer
    const [selectedMisconception, setSelectedMisconception] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    // React Flow state
    const [showFlow, setShowFlow] = useState(false);
    const [flowReady, setFlowReady] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);

    // Edge types for React Flow (must be memoized)
    const edgeTypes = useMemo(() => ({
        bezier: BezierEdge
    }), []);

    // Handle category selection
    const handleCategorySelect = useCallback((category) => {
        setSelectedCategory(category);
    }, []);

    // Handle keyboard navigation for category icons
    const handleCategoryKeyDown = useCallback((e, category) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCategorySelect(category);
        }
    }, [handleCategorySelect]);

    // Handle "Dive Deeper" click
    const handleDiveDeeper = useCallback((misconception) => {
        setSelectedMisconception(misconception);
        setIsDrawerOpen(true);
    }, []);

    // Handle drawer close
    const handleCloseDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        // Wait for animation to complete before clearing misconception
        setTimeout(() => setSelectedMisconception(null), 300);
    }, []);

    // Get element position relative to container
    const getElementPosition = useCallback((element) => {
        const container = document.querySelector('.common-misconceptions-page');
        if (!element || !container) {
            return { x: 0, y: 0 };
        }

        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        return {
            x: elementRect.left - containerRect.left + (elementRect.width / 2),
            y: elementRect.top - containerRect.top + (elementRect.height / 2)
        };
    }, []);

    // Update nodes and edges for React Flow
    const updateFlowElements = useCallback(() => {
        if (!selectedCategory) return;

        const newNodes = [];
        const newEdges = [];

        // Create nodes for each category icon
        sortedCategories.forEach((category) => {
            const iconElement = document.getElementById(`category-icon-${category.category_id}`);
            if (iconElement) {
                const position = getElementPosition(iconElement);
                // Push the node down from center to avoid line appearing near adjacent icons
                const elementRect = iconElement.getBoundingClientRect();
                position.y = position.y + (elementRect.height * 0.3); // Push down by 30% of icon height
                newNodes.push({
                    id: `category-icon-${category.category_id}`,
                    type: 'categoryIconNode',
                    position: position,
                    data: {}
                });
            }
        });

        // Create node for the info box top
        const infoBoxElement = document.getElementById('category-info-box-target');
        if (infoBoxElement) {
            const position = getElementPosition(infoBoxElement);
            newNodes.push({
                id: 'info-box-target',
                type: 'infoBoxNode',
                position: position,
                data: {}
            });

            // Create edge from selected category icon to info box
            const sourceId = `category-icon-${selectedCategory.category_id}`;
            const targetId = 'info-box-target';

            if (newNodes.find(n => n.id === sourceId) && newNodes.find(n => n.id === targetId)) {
                newEdges.push({
                    id: `edge-${sourceId}-${targetId}`,
                    source: sourceId,
                    target: targetId,
                    type: 'bezier',
                    animated: true,
                    style: {
                        stroke: 'var(--page_background)',
                        strokeWidth: 3,
                        strokeLinecap: 'round',
                        strokeDasharray: 5,
                        zIndex: -1
                    }
                });
            }
        }

        if (newNodes.length > 0) {
            setNodes(newNodes);
        }
        if (newEdges.length > 0) {
            setEdges(newEdges);
            setFlowReady(true);
        }
    }, [selectedCategory, sortedCategories, getElementPosition]);

    // Close drawer on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isDrawerOpen) {
                handleCloseDrawer();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isDrawerOpen, handleCloseDrawer]);

    // Prevent body scroll when drawer is open
    useEffect(() => {
        if (isDrawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isDrawerOpen]);

    // Update React Flow when showFlow becomes true or selectedCategory changes
    useEffect(() => {
        if (showFlow) {
            setFlowReady(false);
            // Delay to ensure DOM is fully rendered
            const timeout = setTimeout(() => {
                updateFlowElements();
            }, 100);

            // Update on window resize
            const handleResize = () => {
                updateFlowElements();
            };

            window.addEventListener('resize', handleResize);

            return () => {
                clearTimeout(timeout);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [showFlow, selectedCategory, updateFlowElements]);

    return (
        <>
            <HelmetProvider pageType="commonMisconceptions" />

            <div className="static-page-content-frame common-misconceptions-page">
                <div className="common-misconceptions-intro text-align-center">
                    <h1>{title}</h1>
                    <p>{introText}</p>
                </div>

                {/* Category Icon Selector */}
                <div className={`common-misconceptions-category-selector ${deviceType}`}>
                    {sortedCategories.map((category) => {
                        const isSelected = selectedCategory.category_id === category.category_id;
                        const imageUrl = CreateCloudfrontUrl(category.category_image);

                        return (
                            <div
                                key={category.category_id}
                                id={`category-icon-${category.category_id}`}
                                className={`category-icon ${isSelected ? 'selected' : ''} ${deviceType}`}
                                onClick={() => handleCategorySelect(category)}
                                onKeyDown={(e) => handleCategoryKeyDown(e, category)}
                                role="button"
                                tabIndex={0}
                                aria-label={`Select ${category.category_headline}`}
                                aria-selected={isSelected}
                            >
                                <div className="category-icon-background" style={{ backgroundImage: `url(${imageUrl})` }}>
                                    <div className="category-icon-overlay"></div>
                                    <span className="category-icon-text">{category.category_headline}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Selected Category Info */}
                {selectedCategory && (
                    <div className="common-misconceptions-category-info">
                        <div id="category-info-box-target" style={{ position: 'absolute', top: 0, left: '50%', width: 1, height: 1 }}></div>
                        {/* <h2>{selectedCategory.category_headline}</h2> */}
                        <h2 className="common-misconceptions-category-info-text">{selectedCategory.category_text}</h2>
                        <div className="common-misconceptions-cards-container">
                            {selectedCategory.misconceptions.map((misconception) => (
                                <article key={misconception.id} className="misconception-card">
                                    <div className="misconception-card-content">
                                        <div className="misconception-section">
                                            <span className="card-label misconception-label">Misconception</span>
                                            <h3>{misconception.misconception}</h3>
                                        </div>
                                        <div className="reality-section">
                                            <span className="card-label reality-label">Reality</span>
                                            <p>{misconception.reality_short}</p>
                                        </div>
                                    </div>
                                    <button
                                        className="dive-deeper-button"
                                        onClick={() => handleDiveDeeper(misconception)}
                                        aria-label={`Learn more about: ${misconception.misconception}`}
                                    >
                                        Dive Deeper
                                    </button>
                                </article>
                            ))}
                        </div>
                    </div>
                )}

                {/* ReactFlow for animated line */}
                {(showFlow && flowReady && nodes.length > 0 && edges.length > 0) && (
                    <div className="reactflow-wrapper">
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            nodeTypes={nodeTypes}
                            edgeTypes={edgeTypes}
                            fitView={false}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            zoomOnScroll={false}
                            panOnScroll={false}
                            preventScrolling={true}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                                zIndex: 1
                            }}
                        />
                    </div>
                )}

                {/* FlowTrigger must be at the END */}
                <FlowTrigger onMount={() => setShowFlow(true)} />
            </div>

            {/* Slide-in Drawer Overlay */}
            {isDrawerOpen && (
                <div className="drawer-overlay" onClick={handleCloseDrawer}>
                    <div
                        className={`drawer-panel ${isDrawerOpen ? 'open' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className="drawer-close-button"
                            onClick={handleCloseDrawer}
                            aria-label="Close drawer"
                        >
                            <CircleX size={24} />
                        </div>

                        {selectedMisconception && (
                            <div className="drawer-content">
                                <div className="drawer-misconception">
                                    <span className="drawer-label">Misconception</span>
                                    <h2>{selectedMisconception.misconception}</h2>
                                </div>

                                <div className="drawer-reality">
                                    <span className="drawer-label reality">Reality</span>
                                    <p dangerouslySetInnerHTML={{ __html: selectedMisconception.reality_long }} />
                                </div>

                                <div className="drawer-quoll-position">
                                    <span className="drawer-label quoll">Our Take</span>
                                    <p>{selectedMisconception.quoll_position}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default CommonMisconceptions;
